import { supabase } from '../lib/supabase';
import { videosService } from './videosService';
import { generateStorageThumbnailUrl } from '../utils/thumbnailUtils';

// Helper function to extract video ID from YouTube URL
const extractYouTubeVideoId = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Helper function to extract video ID from Twitch URL
const extractTwitchVideoId = (url: string): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }
  const regex = /twitch\.tv\/videos\/(\d+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Helper function to generate thumbnail URL
const generateThumbnailUrl = (videoUrl: string): string | null => {
  if (!videoUrl || typeof videoUrl !== 'string') {
    return null;
  }
  
  const youtubeId = extractYouTubeVideoId(videoUrl);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  }
  
  const twitchId = extractTwitchVideoId(videoUrl);
  if (twitchId) {
    // Twitch doesn't have a direct thumbnail API, so we'll use a placeholder
    // In the future, we could use Twitch API to get actual thumbnails
    return `https://static-cdn.jtvnw.net/ttv-static/404_boxart-272x380.jpg`;
  }
  
  return null;
};

// Helper to extract storage video UUID from CDN URL or path
const extractStorageVideoId = (videoUrl: string): string | null => {
  if (!videoUrl || typeof videoUrl !== 'string') return null;
  try {
    let path = videoUrl.trim();
    if (path.startsWith('http')) {
      const url = new URL(path);
      path = url.pathname;
    }
    const segments = path.replace(/^\/+/, '').split('/');
    if (segments.length < 2) return null;
    const maybeVideoId = segments[1];
    const uuidRegex = /^[0-9a-fA-F-]{36}$/;
    if (uuidRegex.test(maybeVideoId)) {
      return maybeVideoId;
    }
  } catch (error) {
    console.warn('Failed to parse storage video id', { videoUrl, error });
  }
  return null;
};

export type NewMatchVideoInput = {
  url: string;
  order: number;
  matchStartVideoTimeSeconds?: number | null;
  matchStartMatchMinute?: number | null;
  sourceType?: 'youtube' | 'storage';
  storageVideoId?: string | null;
};

export const matchVideosService = {
  // Test function to check table access
  testTableAccess: async () => {
    try {
      // Test Supabase connection
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Test access to match_videos table
      const { error } = await supabase
        .from('match_videos')
        .select('id')
        .limit(1);

      if (error) {
        return { success: false, error: `Database error: ${error.message}` };
      }

      return { 
        success: true, 
        message: 'Video service connection successful',
        user: user.email 
      };
    } catch (error) {
      return { 
        success: false, 
        error: `Connection error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      };
    }
  },

  /**
   * Get match IDs that already have at least one video linked (for filtering dropdowns).
   * Used so "Link to Match" only shows matches that don't already have a video.
   */
  getMatchIdsWithLinkedVideo: async (): Promise<{ success: true; data: string[] } | { success: false; error: string }> => {
    try {
      const { data, error } = await supabase
        .from('match_videos')
        .select('match_id')
        .not('match_id', 'is', null);

      if (error) {
        return { success: false, error: error.message };
      }

      const matchIds = [...new Set((data || []).map((row) => row.match_id).filter(Boolean))] as string[];
      return { success: true, data: matchIds };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to fetch match IDs with linked videos',
      };
    }
  },

  // Get all videos for a specific match
  getMatchVideos: async (matchId: string) => {
    try {
      const { data, error } = await supabase
        .from('match_videos')
        .select('*')
        .eq('match_id', matchId)
        .order('video_order', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch videos: ${error.message}`);
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Add videos to a match
  addMatchVideos: async (
    matchId: string, 
    videoInputs: NewMatchVideoInput[], 
    userId?: string
  ) => {
    try {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to add videos');
      }

      // Check existing video count
      const { data: existingVideos } = await supabase
        .from('match_videos')
        .select('id')
        .eq('match_id', matchId);

      const currentCount = existingVideos?.length || 0;
      const newCount = videoInputs.length;
      
      if (currentCount + newCount > 5) {
        throw new Error(`Maximum 5 videos allowed per match. Current: ${currentCount}, Trying to add: ${newCount}`);
      }

      // Validate storage videos exist and are ready before inserting
      const storageVideoInputs = videoInputs.filter(input => input.sourceType === 'storage');
      if (storageVideoInputs.length > 0) {
        for (const input of storageVideoInputs) {
          // Try to find video by URL/path first (handles case where folder UUID != video ID)
          let videoCheck = await videosService.getVideoByPath(input.url);
          
          // If not found by path, try by extracted video ID as fallback
          if (!videoCheck.success || !videoCheck.data) {
            const storageVideoId = input.storageVideoId || extractStorageVideoId(input.url);
            if (storageVideoId) {
              videoCheck = await videosService.getVideoById(storageVideoId);
            }
          }

          if (!videoCheck.success || !videoCheck.data) {
            throw new Error(`Video not found for URL: ${input.url}. Please ensure the video exists in Capture and is fully processed.`);
          }

          // Check if video is ready (not still processing)
          if (videoCheck.data.status !== 'ready') {
            throw new Error(`Video is still ${videoCheck.data.status}. Please wait for processing to complete before linking to a match.`);
          }

          // Store the actual video ID for the insert (not the folder UUID)
          // Update the input with the correct video ID
          input.storageVideoId = videoCheck.data.id;
        }
      }

      // Prepare video data
      const videosToInsert = videoInputs.map((input) => {
        const isStorage = input.sourceType === 'storage';
        const storageVideoId = isStorage
          ? input.storageVideoId || extractStorageVideoId(input.url)
          : null;
        const videoSourceType = isStorage ? 'cloudflare_r2' : 'youtube';

        // Initialize timing_points array if timing data is provided
        let timingPoints: any[] = [];
        if (input.matchStartVideoTimeSeconds !== null && input.matchStartVideoTimeSeconds !== undefined) {
          timingPoints = [{
            match_minute: input.matchStartMatchMinute ?? 0,
            video_time_seconds: input.matchStartVideoTimeSeconds,
            event_type: 'match_start',
            source_log_id: null
          }];
        }

        // Generate thumbnail URL based on video type
        const thumbnailUrl = isStorage 
          ? generateStorageThumbnailUrl(input.url, 0) 
          : generateThumbnailUrl(input.url);

        return {
          match_id: matchId,
          video_url: input.url,
          thumbnail_url: thumbnailUrl,
          video_order: input.order,
          match_start_video_time_seconds: input.matchStartVideoTimeSeconds ?? null,
          match_start_match_minute: input.matchStartMatchMinute ?? 0,
          timing_points: timingPoints.length > 0 ? timingPoints : [],
          created_by: userId || user.id,
          updated_by: userId || user.id,
          video_source_type: videoSourceType,
          storage_video_id: storageVideoId,
        };
      });

      // Insert videos
      const { data, error } = await supabase
        .from('match_videos')
        .insert(videosToInsert)
        .select();

      if (error) {
        throw new Error(`Failed to add videos: ${error.message}`);
      }

      return { 
        success: true, 
        data: data,
        message: `${videoInputs.length} video${videoInputs.length > 1 ? 's' : ''} added successfully` 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Replace all videos for a match (delete existing and add new)
  replaceMatchVideos: async (
    matchId: string, 
    videoInputs: NewMatchVideoInput[], 
    userId?: string
  ) => {
    try {
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to replace videos');
      }

      // Validate video count
      if (videoInputs.length > 5) {
        throw new Error(`Maximum 5 videos allowed per match. Trying to add: ${videoInputs.length}`);
      }

      // Get existing video IDs before deletion (to null out foreign key references)
      const { data: existingVideos } = await supabase
        .from('match_videos')
        .select('id')
        .eq('match_id', matchId);

      const existingVideoIds = existingVideos?.map(v => v.id) || [];

      // Null out video_id in scoresheet_logs that reference these videos
      // This preserves the audit trail while allowing video deletion
      if (existingVideoIds.length > 0) {
        const { error: nullifyError } = await supabase
          .from('scoresheet_logs')
          .update({ video_id: null })
          .eq('match_id', matchId)
          .in('video_id', existingVideoIds);

        if (nullifyError) {
          throw new Error(`Failed to update scoresheet logs: ${nullifyError.message}`);
        }
      }

      // Delete all existing videos for this match
      const { error: deleteError } = await supabase
        .from('match_videos')
        .delete()
        .eq('match_id', matchId);

      if (deleteError) {
        throw new Error(`Failed to delete existing videos: ${deleteError.message}`);
      }

      // If no new videos to add, return success
      if (videoInputs.length === 0) {
        return { 
          success: true, 
          data: [],
          message: 'All videos removed successfully' 
        };
      }

      // Validate storage videos exist and are ready before inserting
      const storageVideoInputs = videoInputs.filter(input => input.sourceType === 'storage');
      if (storageVideoInputs.length > 0) {
        for (const input of storageVideoInputs) {
          // Try to find video by URL/path first (handles case where folder UUID != video ID)
          let videoCheck = await videosService.getVideoByPath(input.url);
          
          // If not found by path, try by extracted video ID as fallback
          if (!videoCheck.success || !videoCheck.data) {
            const storageVideoId = input.storageVideoId || extractStorageVideoId(input.url);
            if (storageVideoId) {
              videoCheck = await videosService.getVideoById(storageVideoId);
            }
          }

          if (!videoCheck.success || !videoCheck.data) {
            throw new Error(`Video not found for URL: ${input.url}. Please ensure the video exists in Capture and is fully processed.`);
          }

          // Check if video is ready (not still processing)
          if (videoCheck.data.status !== 'ready') {
            throw new Error(`Video is still ${videoCheck.data.status}. Please wait for processing to complete before linking to a match.`);
          }

          // Store the actual video ID for the insert (not the folder UUID)
          // Update the input with the correct video ID
          input.storageVideoId = videoCheck.data.id;
        }
      }

      // Prepare new video data
      const videosToInsert = videoInputs.map((input) => {
        const isStorage = input.sourceType === 'storage';
        const storageVideoId = isStorage
          ? input.storageVideoId || extractStorageVideoId(input.url)
          : null;
        const videoSourceType = isStorage ? 'cloudflare_r2' : 'youtube';

        // Initialize timing_points array if timing data is provided
        let timingPoints: any[] = [];
        if (input.matchStartVideoTimeSeconds !== null && input.matchStartVideoTimeSeconds !== undefined) {
          timingPoints = [{
            match_minute: input.matchStartMatchMinute ?? 0,
            video_time_seconds: input.matchStartVideoTimeSeconds,
            event_type: 'match_start',
            source_log_id: null
          }];
        }

        // Generate thumbnail URL based on video type
        const thumbnailUrl = isStorage 
          ? generateStorageThumbnailUrl(input.url, 0) 
          : generateThumbnailUrl(input.url);

        return {
          match_id: matchId,
          video_url: input.url,
          thumbnail_url: thumbnailUrl,
          video_order: input.order,
          match_start_video_time_seconds: input.matchStartVideoTimeSeconds ?? null,
          match_start_match_minute: input.matchStartMatchMinute ?? 0,
          timing_points: timingPoints.length > 0 ? timingPoints : [],
          created_by: userId || user.id,
          updated_by: userId || user.id,
          video_source_type: videoSourceType,
          storage_video_id: storageVideoId,
        };
      });

      // Insert new videos
      const { data, error } = await supabase
        .from('match_videos')
        .insert(videosToInsert)
        .select();

      if (error) {
        throw new Error(`Failed to add videos: ${error.message}`);
      }

      return { 
        success: true, 
        data: data,
        message: `${videoInputs.length} video${videoInputs.length > 1 ? 's' : ''} updated successfully` 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Update video order
  updateVideoOrder: async (videoId: string, newOrder: number, userId?: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to update videos');
      }

      const { data, error } = await supabase
        .from('match_videos')
        .update({ 
          video_order: newOrder,
          updated_by: userId || user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', videoId)
        .select();

      if (error) {
        throw new Error(`Failed to update video order: ${error.message}`);
      }

      return { success: true, data: data?.[0] };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Delete a video
  deleteVideo: async (videoId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to delete videos');
      }

      const { error } = await supabase
        .from('match_videos')
        .delete()
        .eq('id', videoId);

      if (error) {
        throw new Error(`Failed to delete video: ${error.message}`);
      }

      return { success: true, message: 'Video deleted successfully' };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Delete all videos for a match
  deleteMatchVideos: async (matchId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to delete videos');
      }

      const { error } = await supabase
        .from('match_videos')
        .delete()
        .eq('match_id', matchId);

      if (error) {
        throw new Error(`Failed to delete match videos: ${error.message}`);
      }

      return { success: true, message: 'All match videos deleted successfully' };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Helper function to validate video URL
  validateVideoUrl: (url: string): { isValid: boolean; platform: string | null; error?: string } => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[\w-]+/;
    const twitchRegex = /^(https?:\/\/)?(www\.)?(twitch\.tv\/videos\/\d+|twitch\.tv\/\w+\/clip\/[\w-]+)/;

    if (youtubeRegex.test(url)) {
      return { isValid: true, platform: 'youtube' };
    } else if (twitchRegex.test(url)) {
      return { isValid: true, platform: 'twitch' };
    } else {
      return { 
        isValid: false, 
        platform: null, 
        error: 'Please enter a valid YouTube or Twitch URL' 
      };
    }
  },

  // Get timing points for a video
  getTimingPoints: async (videoId: string) => {
    try {
      const { data, error } = await supabase
        .from('match_videos')
        .select('timing_points')
        .eq('id', videoId)
        .single();

      if (error) {
        throw new Error(`Failed to fetch timing points: ${error.message}`);
      }

      return { 
        success: true, 
        data: (data?.timing_points as any[]) || [] 
      };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Add timing point to video
  addTimingPoint: async (
    videoId: string,
    matchMinute: number,
    videoTimeSeconds: number,
    eventType: string,
    sourceLogId?: string | null
  ) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User must be authenticated to add timing points');
      }

      // Get current timing points
      const { data: video, error: fetchError } = await supabase
        .from('match_videos')
        .select('timing_points')
        .eq('id', videoId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch video: ${fetchError.message}`);
      }

      const currentTimingPoints = (video?.timing_points as any[]) || [];

      // Check if timing point for this event type already exists
      const existingIndex = currentTimingPoints.findIndex(
        (point: any) => point.event_type === eventType
      );

      const newTimingPoint = {
        match_minute: matchMinute,
        video_time_seconds: videoTimeSeconds,
        event_type: eventType,
        source_log_id: sourceLogId || null
      };

      let updatedTimingPoints: any[];
      if (existingIndex >= 0) {
        // Update existing timing point
        updatedTimingPoints = [...currentTimingPoints];
        updatedTimingPoints[existingIndex] = newTimingPoint;
      } else {
        // Add new timing point
        updatedTimingPoints = [...currentTimingPoints, newTimingPoint];
      }

      // Sort by video_time_seconds for easier calculation
      updatedTimingPoints.sort((a, b) => a.video_time_seconds - b.video_time_seconds);

      // Update video with new timing points
      const { error: updateError } = await supabase
        .from('match_videos')
        .update({
          timing_points: updatedTimingPoints,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', videoId);

      if (updateError) {
        throw new Error(`Failed to update timing points: ${updateError.message}`);
      }

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  },

  // Initialize timing points from match_start fields (for existing videos)
  initializeTimingPoints: async (videoId: string) => {
    try {
      const { data: video, error: fetchError } = await supabase
        .from('match_videos')
        .select('match_start_video_time_seconds, match_start_match_minute, timing_points')
        .eq('id', videoId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch video: ${fetchError.message}`);
      }

      // If timing_points already exists and is not empty, skip
      if (video?.timing_points && Array.isArray(video.timing_points) && video.timing_points.length > 0) {
        return { success: true, message: 'Timing points already initialized' };
      }

      // Create initial timing point from match_start fields
      if (video?.match_start_video_time_seconds !== null || video?.match_start_match_minute !== null) {
        const initialTimingPoint = [{
          match_minute: video.match_start_match_minute ?? 0,
          video_time_seconds: video.match_start_video_time_seconds ?? 0,
          event_type: 'match_start',
          source_log_id: null
        }];

        const { error: updateError } = await supabase
          .from('match_videos')
          .update({ timing_points: initialTimingPoint })
          .eq('id', videoId);

        if (updateError) {
          throw new Error(`Failed to initialize timing points: ${updateError.message}`);
        }

        return { success: true, message: 'Timing points initialized' };
      }

      return { success: true, message: 'No timing data to initialize' };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
};
